/**
 * Funnel rules, kept free of Angular so they can be tested on their own.
 *
 * A project's funnel is an ordered list of stages (earliest first), each with a
 * temperature and a priority (P0 is the highest). Every outcome the project can
 * produce is mapped to a stage, or to "out" when it ends the journey, and may be
 * flagged as a goal. The server applies the same checks (funnel_helper.py); this
 * file only exists so the screen can say what is wrong before saving.
 */

export const OUT_STAGE = 'out';
export const STAGE_TEMPS = ['cold', 'warm', 'hot'];
export const MAX_STAGES = 12;
export const MAX_PRIORITY = 9;
export const MAX_STAGE_NAME = 40;
export const MIN_STAGES = 2;

export interface FunnelStage {
  id: string;
  name: string;
  temp: string;
  pri: number;
}

export interface OutcomeMapping {
  stage: string;
  goal: boolean;
}

export interface FunnelConfig {
  stages: FunnelStage[];
  outcome_map: { [outcome: string]: OutcomeMapping };
  /** Credits per goal the owner is aiming for. Optional. */
  target_cost_per_goal?: number | null;
}

/** One day or week of the cost per goal trend. */
export interface CostTrendPoint {
  date: string;
  credits: number | null;
  goals: number;
  cost_per_goal: number | null;
  cumulative_credits: number | null;
  cumulative_goals: number;
  cumulative_cost_per_goal: number | null;
}

export interface FunnelMove {
  from_stage: string;
  to_stage: string;
  from_name: string;
  to_name: string;
  reached_from: number;
  reached_to: number;
  conversion: number | null;
  credits_spent: number | null;
  credits_per_move: number | null;
  voice_credits_per_move: number | null;
  whatsapp_credits_per_move: number | null;
  recommended_channel: string | null;
}

export interface FunnelStageMetric {
  id: string;
  name: string;
  temp: string;
  pri: number;
  role: string | null;
  reached: number;
  currently_here: number;
  share_of_leads: number | null;
  credits_spent?: number | null;
  credits_voice?: number | null;
  credits_whatsapp?: number | null;
}

export interface FunnelSummary {
  total_leads: number;
  leads_with_activity: number;
  leads_left_funnel: number;
  goal_leads: number;
  goal_outcomes: string[];
  total_credits: number | null;
  cost_per_goal_credits: number | null;
  target_cost_per_goal?: number | null;
  credit_rate_inr: number | null;
  total_cost_inr: number | null;
  cost_per_goal_inr: number | null;
}

export interface FunnelMetricsResponse {
  configured: boolean;
  start_date?: string;
  end_date?: string;
  rows_read?: number;
  truncated?: boolean;
  stages?: FunnelStageMetric[];
  moves?: FunnelMove[];
  summary?: FunnelSummary;
  unmapped_outcomes?: { outcome: string; count: number }[];
  trend?: CostTrendPoint[];
}

export function cloneConfig(config: FunnelConfig): FunnelConfig {
  return JSON.parse(JSON.stringify(config));
}

/** A sensible starting funnel, so nobody begins from a blank screen. */
export function defaultFunnel(): FunnelConfig {
  return {
    stages: [
      { id: 'new-lead', name: 'New lead', temp: 'cold', pri: 3 },
      { id: 'connected', name: 'Connected', temp: 'warm', pri: 2 },
      { id: 'interested', name: 'Interested', temp: 'warm', pri: 1 },
      { id: 'booked', name: 'Booked', temp: 'hot', pri: 0 }
    ],
    outcome_map: {}
  };
}

/** The config stored on the project, or null when there is none yet. */
export function readFunnel(project: any): FunnelConfig | null {
  const raw = project ? project.funnel_config : null;
  if (!raw || !Array.isArray(raw.stages) || raw.stages.length === 0) {
    return null;
  }
  const config: FunnelConfig = { stages: raw.stages, outcome_map: raw.outcome_map || {} };
  if (typeof raw.target_cost_per_goal === 'number' && raw.target_cost_per_goal > 0) {
    config.target_cost_per_goal = raw.target_cost_per_goal;
  }
  return cloneConfig(config);
}

/** Outcome names the project can produce, in the order the owner defined them. */
export function outcomeNames(project: any): string[] {
  const list = project && Array.isArray(project.expected_outcomes) ? project.expected_outcomes : [];
  const seen: { [k: string]: boolean } = {};
  const names: string[] = [];
  for (const item of list) {
    const name = String(typeof item === 'string' ? item : (item && item.outcome_name) || '').trim();
    const key = name.toLowerCase();
    if (name && !seen[key]) {
      seen[key] = true;
      names.push(name);
    }
  }
  return names;
}

/** Stage ids are lowercase slugs and never "out", which is reserved. */
export function makeStageId(name: string, taken: string[]): string {
  let base = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);
  if (!base || base === OUT_STAGE) {
    base = 'stage';
  }
  let id = base;
  let n = 2;
  while (taken.indexOf(id) !== -1) {
    id = base + '-' + n;
    n++;
  }
  return id;
}

/** P0 for the final stage, counting up towards the first. */
export function autoPriorities(config: FunnelConfig): FunnelConfig {
  const next = cloneConfig(config);
  const last = next.stages.length - 1;
  next.stages.forEach((stage, i) => {
    stage.pri = Math.min(last - i, MAX_PRIORITY);
  });
  return next;
}

export function roleFor(pri: number): string {
  if (pri === 0) { return 'primary'; }
  if (pri === 1) { return 'secondary'; }
  if (pri === 2) { return 'tertiary'; }
  return '';
}

export function addStage(config: FunnelConfig, name: string): FunnelConfig {
  const next = cloneConfig(config);
  if (next.stages.length >= MAX_STAGES) {
    return next;
  }
  const clean = String(name || '').trim() || 'New stage';
  next.stages.push({
    id: makeStageId(clean, next.stages.map(s => s.id)),
    name: clean,
    temp: 'hot',
    pri: 0
  });
  return autoPriorities(next);
}

/** Removing a stage un-maps the outcomes that pointed at it. */
export function removeStage(config: FunnelConfig, id: string): FunnelConfig {
  const next = cloneConfig(config);
  next.stages = next.stages.filter(s => s.id !== id);
  Object.keys(next.outcome_map).forEach(name => {
    if (next.outcome_map[name].stage === id) {
      delete next.outcome_map[name];
    }
  });
  return autoPriorities(next);
}

export function moveStage(config: FunnelConfig, index: number, delta: number): FunnelConfig {
  const next = cloneConfig(config);
  const to = index + delta;
  if (index < 0 || index >= next.stages.length || to < 0 || to >= next.stages.length) {
    return next;
  }
  const moved = next.stages.splice(index, 1)[0];
  next.stages.splice(to, 0, moved);
  return autoPriorities(next);
}

/** Map an outcome to a stage, to "out", or (empty id) to nothing. */
export function setOutcomeStage(config: FunnelConfig, outcome: string, stageId: string): FunnelConfig {
  const next = cloneConfig(config);
  if (!stageId) {
    delete next.outcome_map[outcome];
    return next;
  }
  const current = next.outcome_map[outcome];
  next.outcome_map[outcome] = {
    stage: stageId,
    // An outcome that ends the journey can never be a goal.
    goal: stageId !== OUT_STAGE && !!(current && current.goal)
  };
  return next;
}

export function setOutcomeGoal(config: FunnelConfig, outcome: string, goal: boolean): FunnelConfig {
  const next = cloneConfig(config);
  const current = next.outcome_map[outcome];
  if (current && current.stage !== OUT_STAGE) {
    current.goal = goal;
  }
  return next;
}

export function goalOutcomes(config: FunnelConfig): string[] {
  return Object.keys(config.outcome_map).filter(name => config.outcome_map[name].goal).sort();
}

/** Outcomes of the project that are not placed anywhere yet. */
export function unmappedOutcomes(config: FunnelConfig, names: string[]): string[] {
  return names.filter(name => !config.outcome_map[name]);
}

/** Outcomes grouped for the settings table: one list per stage, plus "out". */
export function outcomesByStage(config: FunnelConfig): { [stageId: string]: string[] } {
  const grouped: { [stageId: string]: string[] } = {};
  config.stages.forEach(s => (grouped[s.id] = []));
  grouped[OUT_STAGE] = [];
  Object.keys(config.outcome_map).forEach(name => {
    const stage = config.outcome_map[name].stage;
    if (grouped[stage]) {
      grouped[stage].push(name);
    }
  });
  return grouped;
}

/** Everything wrong with a config, in plain words. Empty means it can be saved. */
export function validateFunnel(config: FunnelConfig): string[] {
  const errors: string[] = [];
  if (config.stages.length < MIN_STAGES) {
    errors.push('Add at least two stages.');
  }
  if (config.stages.length > MAX_STAGES) {
    errors.push('A funnel can have at most ' + MAX_STAGES + ' stages.');
  }
  const names: { [k: string]: boolean } = {};
  config.stages.forEach((stage, i) => {
    const label = 'Stage ' + (i + 1);
    const name = (stage.name || '').trim();
    if (!name) {
      errors.push(label + ' needs a name.');
    } else if (name.length > MAX_STAGE_NAME) {
      errors.push(label + ' name is longer than ' + MAX_STAGE_NAME + ' characters.');
    } else if (names[name.toLowerCase()]) {
      errors.push('Two stages are called "' + name + '".');
    }
    names[name.toLowerCase()] = true;
    if (STAGE_TEMPS.indexOf(stage.temp) === -1) {
      errors.push(label + ' needs a temperature.');
    }
    if (!(stage.pri >= 0 && stage.pri <= MAX_PRIORITY) || Math.floor(stage.pri) !== stage.pri) {
      errors.push(label + ' priority must be a whole number from 0 to ' + MAX_PRIORITY + '.');
    }
  });
  const ids = config.stages.map(s => s.id);
  Object.keys(config.outcome_map).forEach(name => {
    const mapping = config.outcome_map[name];
    if (mapping.stage !== OUT_STAGE && ids.indexOf(mapping.stage) === -1) {
      errors.push('"' + name + '" points to a stage that no longer exists.');
    }
    if (mapping.goal && mapping.stage === OUT_STAGE) {
      errors.push('"' + name + '" ends the journey, so it cannot be a goal.');
    }
  });
  const target = config.target_cost_per_goal;
  if (target !== undefined && target !== null && (typeof target !== 'number' || !isFinite(target) || target <= 0)) {
    errors.push('The cost per goal target must be a number above 0.');
  }
  return errors;
}

/** What goes to the server. Names are trimmed; nothing else changes. */
export function toPayload(config: FunnelConfig): FunnelConfig {
  const next = cloneConfig(config);
  next.stages.forEach(s => (s.name = (s.name || '').trim()));
  const target = next.target_cost_per_goal;
  if (typeof target === 'number' && target > 0) {
    next.target_cost_per_goal = Math.round(target * 100) / 100;
  } else {
    delete next.target_cost_per_goal;
  }
  return next;
}

export function sameFunnel(a: FunnelConfig | null, b: FunnelConfig | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Credits with a sensible number of decimals; a dash when unknown. */
export function formatCredits(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value as number)) {
    return '–';
  }
  const rounded = Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatPercent(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || isNaN(ratio as number)) {
    return '–';
  }
  return Math.round(ratio * 1000) / 10 + '%';
}

/** Width for a funnel bar: never zero for a stage that was reached at all. */
export function barWidth(reached: number, total: number): number {
  if (!total || reached <= 0) {
    return 0;
  }
  return Math.max(Math.round((reached / total) * 100), 3);
}

/** The move that costs the most credits per lead moved, if any are known. */
export function costliestMove(moves: FunnelMove[]): FunnelMove | null {
  let worst: FunnelMove | null = null;
  moves.forEach(m => {
    if (m.credits_per_move !== null && (worst === null || m.credits_per_move > (worst.credits_per_move as number))) {
      worst = m;
    }
  });
  return worst;
}
